// src/app/services/notification.service.ts
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Subject, of, lastValueFrom } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { NotificationItem, NotificationOverview, NotificationType } from '../../models/notification.model';
import { EnvironmentService } from './environment.service';
import { TranslateService } from '@ngx-translate/core';

export interface PushNotificationEvent {
  /** Numeric notification type matching NotificationType enum (0=Transaction, 1=Msg, 2=Table, 3=Chart). */
  type: NotificationType;
  notificationId: string;
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private _http = inject(HttpClient);
  private _environment = inject(EnvironmentService);
  private _translate = inject(TranslateService);
  private notifSub = new BehaviorSubject<NotificationItem[]>([]);

  /** Emits whenever a push notification is received from MAUI, carrying type and id. */
  private _pushReceived = new Subject<PushNotificationEvent>();
  public pushReceived$ = this._pushReceived.asObservable();

  /** Emits when the user taps a notification in the system tray, carrying type and id. */
  private _pushTapped = new Subject<PushNotificationEvent>();
  public pushTapped$ = this._pushTapped.asObservable();

  /** Called by the MAUI bridge when a push arrives. */
  notifyPushReceived(type: NotificationType, notificationId: string): void {
    this._pushReceived.next({ type, notificationId });
  }

  /** Called by the MAUI bridge when the user taps a notification. */
  notifyPushTapped(type: NotificationType, notificationId: string): void {
    this._pushTapped.next({ type, notificationId });
  }
  public notifications$ = this.notifSub.asObservable();

  async loadNotifications(): Promise<void> {
    const list = await lastValueFrom(
      this._http
        .get<NotificationOverview>('/api/notifications/overview')
        .pipe(
          map(overview => overview.recentNotifications),
          catchError(err => {
            console.error('Failed loading notifications', err);
            return of([]);
          })
        )
    );
    this.notifSub.next(list);
  }

  async markNotificationAsRead(notificationId: string): Promise<void> {
    if (!this._environment.isDesignMode) {
      console.log('[NotificationService] Marking notification as read:', notificationId);

      await lastValueFrom(
        this._http.put(`/api/notifications/${notificationId}/read`, {})
          .pipe(
            catchError(error => {
              console.error('[NotificationService] Error marking notification as read:', error);
              return of(null);
            })
          )
      );

      console.log('[NotificationService] Successfully marked notification as read');
      await this.loadNotifications();
    }
  }

  getReadStatusText(isRead: boolean): string {
    return isRead ? 'GLOBAL.READ' : 'GLOBAL.UNREAD';
  }

  getTranslatedReadStatusText(isRead: boolean): string {
    const key = this.getReadStatusText(isRead);
    return this._translate.instant(key);
  }

  getPreviewText(body: string, maxLength: number = 100): string {
    if (!body) return '';
    return body.length > maxLength ? `${body.substring(0, maxLength)}...` : body;
  }

  getUnreadCountsByType() {
    return this.getOverview().pipe(
      map(overview => Object.fromEntries(
        overview.notificationSummary.map(summary => [summary.type, summary.count])
      ) as Record<number, number>),
      catchError(err => {
        console.error('[NotificationService] Failed loading unread counts:', err);
        return of({} as Record<number, number>);
      })
    );
  }

  getOverview() {
    return this._http.get<NotificationOverview>('/api/notifications/overview');
  }

  getNotificationsByType(type: NotificationType, isRead?: boolean) {
    const params = isRead === undefined
      ? undefined
      : new HttpParams().set('isRead', isRead);

    return this._http.get<NotificationItem[]>(`/api/notifications/type/${type}`, { params });
  }
}
